import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search, Filter, MessageCircle, User, DollarSign, ChevronLeft, ChevronRight } from 'lucide-react';
import './DiscoverAgents.css';

const DiscoverAgents = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [agents, setAgents] = useState([]);
  const [featuredAgents, setFeaturedAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  // TOGGLE: Set to true for scrollable 4-card version, false for 6-card grid version
  const USE_SCROLLABLE_FEATURED = true;
  const [useScrollableFeatured] = useState(USE_SCROLLABLE_FEATURED);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(true);
  const scrollContainerRef = useRef(null);

  // Fetch real agents from API
  const fetchAgents = async () => {
    try {
      setLoading(true);
      
      // Check if we should use mock data (for screenshots/demos)
      const useMockData = window.location.search.includes('mock=true') || 
                         (window.location.hostname === 'localhost' && 
                          localStorage.getItem('useMockAgents') === 'true');
      
      console.log('Mock data detection:', {
        search: window.location.search,
        hostname: window.location.hostname,
        localStorage: localStorage.getItem('useMockAgents'),
        useMockData: useMockData
      });
      
      const mockParam = useMockData ? '&mock=true' : '';
      
          // Add cache-busting parameter to force fresh data
          const response = await fetch(`/api/agents/public?t=${Date.now()}${mockParam}`);
      if (response.ok) {
        const data = await response.json();
        console.log('=== AGENTS DATA DEBUG ===');
        console.log('Total agents fetched:', data.agents?.length || 0);
        console.log('All agents:', data.agents);
        
        // Check isFeatured values
        const agentsWithFeatured = data.agents?.map(agent => ({
          id: agent.id,
          name: agent.name,
          isFeatured: agent.isFeatured,
          is_featured: agent.is_featured, // Check both camelCase and snake_case
          visibility_status: agent.visibility_status
        })) || [];
        console.log('Agents with featured status:', agentsWithFeatured);
        
        const featuredAgentsList = data.agents?.filter(agent => agent.isFeatured === true) || [];
        console.log('Featured agents (isFeatured === true):', featuredAgentsList.length, featuredAgentsList);
        
        const featuredAgentsList2 = data.agents?.filter(agent => agent.isFeatured) || [];
        console.log('Featured agents (truthy isFeatured):', featuredAgentsList2.length, featuredAgentsList2);
        
        const featuredAgentsList3 = data.agents?.filter(agent => agent.is_featured === true) || [];
        console.log('Featured agents (is_featured === true):', featuredAgentsList3.length, featuredAgentsList3);
        
        // Show which agents have which field
        data.agents?.forEach(agent => {
          if (agent.is_featured === true || agent.isFeatured === true) {
            console.log(`Featured agent found: ${agent.name} - is_featured: ${agent.is_featured}, isFeatured: ${agent.isFeatured}`);
          }
        });
        
        console.log('=== END AGENTS DATA DEBUG ===');
        
        setAgents(data.agents || []);
        // Check both camelCase and snake_case field names (database returns snake_case)
        const filteredFeatured = data.agents?.filter(agent => {
          // Check both field name variations
          return agent.isFeatured === true || agent.is_featured === true;
        }) || [];
        // Reverse the order (oldest first instead of newest first)
        const reversedFeatured = [...filteredFeatured].reverse();
        console.log('Setting featured agents state:', reversedFeatured.length, reversedFeatured);
        console.log('Featured agent names:', reversedFeatured.map(a => `${a.name} (id: ${a.id})`));
        setFeaturedAgents(reversedFeatured);
      } else {
        console.error('Failed to fetch agents');
        setAgents([]);
        setFeaturedAgents([]);
      }
    } catch (error) {
      console.error('Error fetching agents:', error);
      setAgents([]);
      setFeaturedAgents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAgents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredAgents = agents.filter(agent => {
    const matchesSearch = agent.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         agent.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         agent.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()));
    
    const matchesCategory = selectedCategory === 'all' || 
                           agent.tags.some(tag => tag === selectedCategory);
    
    return matchesSearch && matchesCategory;
  });

  const handleChatClick = (agent) => {
    // TODO: Implement chat functionality
    console.log('Starting chat with:', agent.name);
    // This will dispatch an event to switch to chat mode
    const event = new CustomEvent('startAgentChat', { detail: { agent } });
    window.dispatchEvent(event);
  };

  // Scroll handlers for scrollable featured section
  const handleScroll = (direction) => {
    if (!scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const cardWidth = container.querySelector('.featured-agent-card')?.offsetWidth || 0;
    const gap = 12; // 0.75rem = 12px
    const scrollAmount = (cardWidth + gap) * 1; // Scroll by 1 card
    
    if (direction === 'right') {
      container.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    } else {
      container.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
    }
  };

  // Update arrow visibility based on scroll position
  useEffect(() => {
    if (!useScrollableFeatured || !scrollContainerRef.current) return;
    
    const container = scrollContainerRef.current;
    const updateArrows = () => {
      const scrollLeft = container.scrollLeft;
      const maxScroll = container.scrollWidth - container.clientWidth;
      const threshold = 10; // Small threshold for rounding
      
      setShowLeftArrow(scrollLeft > threshold); // Show left arrow when scrolled
      setShowRightArrow(scrollLeft < maxScroll - threshold); // Hide right arrow when at end
      setScrollPosition(scrollLeft);
    };
    
    container.addEventListener('scroll', updateArrows);
    updateArrows(); // Initial check
    
    return () => container.removeEventListener('scroll', updateArrows);
  }, [useScrollableFeatured, featuredAgents]);

  if (loading) {
    return (
      <div className="discover-agents">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading agents...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="discover-agents">
        {/* Main Content Area */}
        <div className="content-area">
        {/* Featured Agents Section */}
        {(featuredAgents.length > 0 || agents.length > 0) && (
          <div className={`featured-agents-container ${useScrollableFeatured ? 'scrollable-version' : 'grid-version'}`}>
            <h2 className="featured-agents-title">Featured Agents</h2>
            {useScrollableFeatured ? (
              <div className="featured-agents-scroll-wrapper">
                <div 
                  className="featured-agents-scroll"
                  ref={scrollContainerRef}
                  onMouseEnter={() => {
                    // Arrows will show on hover via CSS
                  }}
                >
                  {(featuredAgents.length > 0 ? featuredAgents : agents.slice(0, 6)).map(agent => (
                    <FeaturedAgentCard key={agent.id} agent={agent} onChatClick={handleChatClick} />
                  ))}
                </div>
                {/* Scroll Arrows */}
                <button
                  className={`featured-scroll-arrow featured-scroll-arrow-left ${showLeftArrow ? 'visible' : ''}`}
                  onClick={() => handleScroll('left')}
                  aria-label="Scroll left"
                >
                  <ChevronLeft size={24} />
                </button>
                <button
                  className={`featured-scroll-arrow featured-scroll-arrow-right ${showRightArrow ? 'visible' : ''}`}
                  onClick={() => handleScroll('right')}
                  aria-label="Scroll right"
                >
                  <ChevronRight size={24} />
                </button>
              </div>
            ) : (
              <div className="featured-agents-grid">
                {(featuredAgents.length > 0 ? featuredAgents : agents.slice(0, 6)).map(agent => (
                  <FeaturedAgentCard key={agent.id} agent={agent} onChatClick={handleChatClick} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* All Agents Section */}
        <div className="all-agents-section">
          <h2 className="section-title">
            All Agents ({filteredAgents.length})
          </h2>
          <div className="agents-grid">
            {filteredAgents.map(agent => (
              <AgentCard key={agent.id} agent={agent} onChatClick={handleChatClick} />
            ))}
          </div>
          {filteredAgents.length === 0 && (
            <div className="no-results">
              <Search size={48} />
              <h3>No agents found</h3>
              <p>Try adjusting your search or filter criteria</p>
            </div>
          )}
        </div>
        </div>

        {/* Footer */}
        <footer className="discover-footer">
          <div className="footer-content">
            <div className="footer-brand">
              <img 
                src={process.env.PUBLIC_URL + "/YourQuestionsAnsweredLogo.png"} 
                alt="Question Answer Pro Logo" 
                width={32} 
                height={32}
                style={{ objectFit: 'contain' }}
              />
              <span>Question Answer Pro</span>
            </div>
            <div className="footer-links">
              <Link to="/privacy">Privacy Policy</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

const AgentCard = ({ agent, onChatClick }) => {
  const hasOwnerEngagement = agent.owner_engagement_fee_cents && agent.owner_engagement_fee_cents > 0;
  const priceDisplay = hasOwnerEngagement ? `$${(agent.owner_engagement_fee_cents / 100).toFixed(2)} for owner engagement` : 'Free';

  return (
    <div 
      className="agent-card"
      onClick={() => onChatClick(agent)}
    >
      <div className="agent-header">
        <div className="agent-avatar discover-agent-avatar">
          {agent.agent_avatar ? (
            <img
              src={agent.agent_avatar}
              alt={agent.name}
              className="discover-agent-avatar-img"
            />
          ) : (
            <User size={20} className="discover-agent-avatar-icon" />
          )}
        </div>
        <div className="agent-pricing">
          <span className={`price-tag ${hasOwnerEngagement ? 'paid' : 'free'}`}>
            {priceDisplay}
          </span>
        </div>
      </div>
      
      <div className="agent-content">
        <h3 className="agent-name">{agent.name}</h3>
        <p className="agent-description">{agent.description}</p>
      </div>
      
      <div className="agent-stats" style={{ display: 'none' }}>
        <MessageCircle size={12} />
        <span>{agent.chat_count || 0} chats</span>
        {hasOwnerEngagement && (
          <div className="paid-indicator">
            <DollarSign size={12} />
            <span>Premium</span>
          </div>
        )}
      </div>
    </div>
  );
};

const FeaturedAgentCard = ({ agent, onChatClick }) => {
  return (
    <div 
      className="featured-agent-card"
      onClick={() => onChatClick(agent)}
    >
      <div className="featured-agent-header">
        <div className="featured-agent-avatar">
          {agent.agent_avatar ? (
            <img
              src={agent.agent_avatar}
              alt={agent.name}
              className="featured-agent-avatar-img"
            />
          ) : (
            <User size={18} className="featured-agent-avatar-icon" />
          )}
        </div>
      </div>
      
      <div className="featured-agent-content">
        <h3 className="featured-agent-name">{agent.name}</h3>
        <p className="featured-agent-description">{agent.description}</p>
      </div>
    </div>
  );
};

export default DiscoverAgents;
